import React from 'react';
import { Alert, Tabs } from 'antd';
import { SpaceConfigurator } from '@mono/space-configurator';

import DatabilityFormGroup from '../../../WidgetConfiguratorComponents/FormGroups/DatabilityFormGroup';
import { FontSize, FormWrapper, NumberField, Switch } from '../../../WidgetConfiguratorComponents/FormGroups';
import { IWidgetConfiguratorProps } from '../../types/IWidgetBlock';
import { SpectraConfig } from '../types';
import SelectOptionFormGroup from '../../../WidgetConfiguratorComponents/FormGroups/SelectOptionFormGroup';
import SliderFormGroup from '../../../WidgetConfiguratorComponents/FormGroups/SliderFormGroup';
import NumberFormGroup from '../../../WidgetConfiguratorComponents/FormGroups/NumberFormGroup';
import TimeRangeFormGroup from '../../../WidgetConfiguratorComponents/FormGroups/TimeRangeFormGroup';
import TextFormGroup from '../../../WidgetConfiguratorComponents/FormGroups/TextFormGroup';
import NumberRangeFormGroup from '../../../WidgetConfiguratorComponents/FormGroups/NumberRangeFormGroup';

const WidgetConfigurator = ({ viewblockState, setViewblockState, baseDatability, componentsToDisplay }: IWidgetConfiguratorProps<SpectraConfig>) => {
    const datability = {
        ...baseDatability,
        ...viewblockState.databilities[0],
    };

    return (
        <Tabs defaultActiveKey='1'>
            <Tabs.TabPane tab='General settings' key='1'>
                <FormWrapper>
                    <TextFormGroup
                        label='Title'
                        value={viewblockState.settings.title}
                        onChange={(title: string) => setViewblockState((prevState) => ({ ...prevState, settings: { ...prevState.settings, title } }))}
                    />

                    <FontSize
                        fontSize={viewblockState.settings.fontSize}
                        onChange={(fontSize: number) => setViewblockState((prevState) => ({ ...prevState, settings: { ...prevState.settings, fontSize } }))}
                    />
                    <Switch
                        label='Use fixed offset'
                        value={viewblockState.settings.useFixedOffset}
                        onChange={(useFixedOffset: boolean) =>
                            setViewblockState((prevState) => ({ ...prevState, settings: { ...prevState.settings, useFixedOffset } }))
                        }
                    />
                    {viewblockState.settings.useFixedOffset && (
                        <NumberField
                            label='Left offset'
                            value={viewblockState.settings.offsetLeft}
                            onChange={(offsetLeft: number) =>
                                setViewblockState((prevState) => ({ ...prevState, settings: { ...prevState.settings, offsetLeft } }))
                            }
                            step={1}
                        />
                    )}
                    <Switch
                        label='Display legend'
                        value={viewblockState.settings.displayLegend}
                        onChange={(displayLegend: boolean) =>
                            setViewblockState((prevState) => ({ ...prevState, settings: { ...prevState.settings, displayLegend } }))
                        }
                    />
                    <Switch
                        label='Display grid'
                        value={viewblockState.settings.displayGrid}
                        onChange={(displayGrid: boolean) =>
                            setViewblockState((prevState) => ({ ...prevState, settings: { ...prevState.settings, displayGrid } }))
                        }
                    />
                    <Switch
                        label='Display time legend'
                        value={viewblockState.settings.displayTimeLegend}
                        onChange={(displayTimeLegend: boolean) =>
                            setViewblockState((prevState) => ({ ...prevState, settings: { ...prevState.settings, displayTimeLegend } }))
                        }
                    />
                    <SliderFormGroup
                        label={'Blur'}
                        value={viewblockState.settings.blur}
                        min={0}
                        max={30}
                        tipFormatter={(val) => `${val}px`}
                        onChange={(blur: number) =>
                            setViewblockState((prevState) => ({ ...prevState, settings: { ...prevState.settings, blur: parseInt(blur.toString()) } }))
                        }
                    />
                </FormWrapper>
            </Tabs.TabPane>

            {componentsToDisplay?.spaceSettings && (
                <Tabs.TabPane tab='Space settings' key='3'>
                    <SpaceConfigurator
                        enableSite
                        initialState={{
                            space: datability.space,
                            movSiteId: viewblockState.settings.movSiteId,
                        }}
                        onChange={(props) => {
                            if (!props.movSiteId) return;
                            setViewblockState((prev) => {
                                return {
                                    ...prev,
                                    databilities: [
                                        {
                                            ...prev.databilities[0],
                                            space: props.space,
                                        },
                                    ],
                                };
                            });
                        }}
                    />
                </Tabs.TabPane>
            )}

            <Tabs.TabPane tab='Data definition' key='2'>
                <FormWrapper>
                    <DatabilityFormGroup
                        datability={datability}
                        onChangeDatability={(datability) => setViewblockState((prevState) => ({ ...prevState, databilities: [datability] }))}
                        dataType={['spectrum']}
                        onInterpolationChange={() => false}
                        disableInterpolation={true}
                        maxRadiusKm={10}
                    />
                    <TimeRangeFormGroup
                        value={datability.timeRange!}
                        range='P-2DT0H--P10DT0H'
                        unit='days'
                        onChange={(timeRange) =>
                            setViewblockState((prevState) => ({ ...prevState, databilities: [{ ...prevState.databilities[0], timeRange }] }))
                        }
                        step={1}
                        markerStep={1}
                    />
                    <SelectOptionFormGroup
                        label={'Y-Axis'}
                        value={viewblockState.settings.yDimensionalVariable}
                        values={[
                            { label: 'Frequency', value: 'freq' },
                            { label: 'Direction', value: 'dir' },
                        ]}
                        onChange={(yDimensionalVariable: 'freq' | 'dir') =>
                            setViewblockState((prevState) => ({ ...prevState, settings: { ...prevState.settings, yDimensionalVariable } }))
                        }
                    />
                    <NumberFormGroup
                        label={'Y-Axis decimal places'}
                        min={0}
                        max={3}
                        value={viewblockState.settings.yAxisDecimalPlaces}
                        onChange={(yAxisDecimalPlaces: number) =>
                            setViewblockState((prevState) => ({ ...prevState, settings: { ...prevState.settings, yAxisDecimalPlaces } }))
                        }
                    />

                    <Switch
                        label='Trim empty Y-Axis values'
                        value={viewblockState.settings.trimEmptyValues}
                        onChange={(trimEmptyValues: boolean) =>
                            setViewblockState((prevState) => ({ ...prevState, settings: { ...prevState.settings, trimEmptyValues } }))
                        }
                    />

                    <NumberRangeFormGroup
                        min={{
                            value: viewblockState.settings.minScaleValue,
                            min: 0,
                            placeholder: 'auto',
                            onChange: (minScaleValue: number) =>
                                setViewblockState((prevState) => ({ ...prevState, settings: { ...prevState.settings, minScaleValue } })),
                        }}
                        max={{
                            value: viewblockState.settings.maxScaleValue,
                            min: 0,
                            placeholder: 'auto',
                            onChange: (maxScaleValue: number) =>
                                setViewblockState((prevState) => ({ ...prevState, settings: { ...prevState.settings, maxScaleValue } })),
                        }}
                        label='Scale bar range'
                    />
                    {typeof viewblockState.settings.minScaleValue === 'number' &&
                    typeof viewblockState.settings.maxScaleValue === 'number' &&
                    viewblockState.settings.minScaleValue >= viewblockState.settings.maxScaleValue ? (
                        <Alert style={{ gridColumn: '1/6' }} message='Max must be bigger than Min scale bar range' type='error' />
                    ) : (
                        ''
                    )}
                </FormWrapper>
            </Tabs.TabPane>
        </Tabs>
    );
};

export default WidgetConfigurator;
